import React, { memo, useMemo, useState } from 'react';
import { BugFilled } from '@ant-design/icons';
import { Empty, Flex, Modal, Switch, Tabs, Tag, Tooltip, Typography } from 'antd';
import { interpolateString } from '@utils/string';
import useQueryDebugLogs from '@hooks/useQueryDebugLogs';
import { CLOSE_BUTTON_LABEL } from '@constants/common';
import { SupportedViewerLanguage } from '@constants/enum/common';
import {
  DEBUG_MODE_TOOLTIP_TITLE,
  NO_LOGS_FOUND_DESCRIPTION,
  OUTPUT_LOGS_MODAL_TAB_TITLE,
  OUTPUT_LOGS_MODAL_TITLE,
} from '@constants/search';
import LoadingIndicator from '@components/common/LoadingIndicator/LoadingIndicator';
import styles from './QueryDebugModal.module.scss';

const CodeViewer = React.lazy(() => import('@components/codeViewer/CodeViewer'));

const { Title } = Typography;

interface IDebugModalProps {
  showToggleDebugMode?: boolean;
}

const QueryDebugModal: React.FC<IDebugModalProps> = ({ showToggleDebugMode = true }) => {
  const {
    debugLogs,
    isDebugLogsModalOpen,
    debugLogsFilters,
    toggleDebugLogsModal,
    getComponentDebugLogs,
    toggleDebugMode,
    isDebugModeEnabled,
  } = useQueryDebugLogs();
  const [activeTabKey, setActiveTabKey] = useState('0');

  const onClose = () => {
    toggleDebugLogsModal({ isVisible: false });
  };

  const getFilteredLogs = () => {
    if (!debugLogsFilters) return debugLogs;

    if (debugLogsFilters.componentName) {
      return getComponentDebugLogs(debugLogsFilters.componentName);
    }

    return debugLogs;
  };

  const handleTabChange = (key: string) => {
    setActiveTabKey(key);
  };

  const logsToDisplay = getFilteredLogs() || [];
  const currentLogData = logsToDisplay[parseInt(activeTabKey, 10)] || {};

  // Renders

  const renderEmptyLogs = () => {
    return (
      <Flex justify="center" align="center" className={styles.emptyLogs}>
        <Empty description={NO_LOGS_FOUND_DESCRIPTION} />
      </Flex>
    );
  };

  const renderFiltersTag = () => {
    if (!debugLogsFilters) return null;

    if (debugLogsFilters.componentName) {
      return <Tag bordered={false}>{debugLogsFilters.componentName}</Tag>;
    }

    return null;
  };

  const renderToggleDebugMode = () => {
    if (!showToggleDebugMode) return null;

    return (
      <Tooltip title={DEBUG_MODE_TOOLTIP_TITLE} placement="bottom">
        <Switch size="small" checked={isDebugModeEnabled} onChange={toggleDebugMode} />
      </Tooltip>
    );
  };

  const tabsItem = useMemo(
    () =>
      logsToDisplay.map((_, index) => ({
        key: index.toString(),
        label: interpolateString(OUTPUT_LOGS_MODAL_TAB_TITLE, { runNumber: index + 1 }),
      })),
    [logsToDisplay],
  );

  const getModalTitle = () => {
    return (
      <Flex align="center" gap="small">
        <BugFilled color="primary" />
        <Title level={5}>{OUTPUT_LOGS_MODAL_TITLE}</Title>
        {renderToggleDebugMode()}
        {renderFiltersTag()}
      </Flex>
    );
  };

  return (
    <Modal
      className={styles.modal}
      title={getModalTitle()}
      open={isDebugLogsModalOpen}
      onCancel={onClose}
      cancelText={CLOSE_BUTTON_LABEL}
      centered
      width="100%"
      footer={null}
    >
      <React.Suspense fallback={<LoadingIndicator />}>
        <Tabs
          activeKey={activeTabKey}
          onChange={handleTabChange}
          className={styles.tabs}
          items={tabsItem}
        />
        {!!logsToDisplay.length ? (
          <CodeViewer
            code={JSON.stringify(currentLogData, null, 2)}
            codeLanguage={SupportedViewerLanguage.JSON}
          />
        ) : (
          renderEmptyLogs()
        )}
      </React.Suspense>
    </Modal>
  );
};

export default memo(QueryDebugModal);
